import React, { useEffect, useRef } from 'react'
import { css } from '@emotion/core'

const OTP_PATTERN = /^\d$/

type OtpInputProps = {
  values: string[]
  onValuesChange: (values: string[]) => void
  ariaLabelPrefix: string
  focusTrigger?: number
}

export const OtpInput: React.FC<OtpInputProps> = ({ values, onValuesChange, ariaLabelPrefix, focusTrigger = 0 }) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const otpLength = values.length

  const updateAt = (index: number, value: string) => {
    const next = [...values]
    next[index] = value
    onValuesChange(next)
  }

  const onOtpChange = (index: number, value: string) => {
    if (!value) {
      updateAt(index, '')
      return
    }

    const normalized = value.slice(-1)
    if (!OTP_PATTERN.test(normalized)) return

    updateAt(index, normalized)

    if (index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const onOtpKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !values[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }

    if (event.key === 'ArrowRight' && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const onOtpPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, otpLength)

    if (!pasted) return

    const next = Array.from({ length: otpLength }, (_, idx) => pasted[idx] || '')
    onValuesChange(next)

    const focusIndex = Math.min(pasted.length, otpLength - 1)
    inputRefs.current[focusIndex]?.focus()
  }

  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [focusTrigger])

  return (
    <div css={otpInputContainerStyle}>
      {values.map((value, idx) => (
        <input
          key={idx}
          ref={el => (inputRefs.current[idx] = el)}
          value={value}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          aria-label={`${ariaLabelPrefix} ${idx + 1}`}
          css={otpInputStyle}
          onChange={event => onOtpChange(idx, event.target.value)}
          onKeyDown={event => onOtpKeyDown(idx, event)}
          onPaste={onOtpPaste}
        />
      ))}
    </div>
  )
}

const otpInputContainerStyle = css({
  marginTop: 28,
  display: 'grid',
  gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
  gap: 8,
})

const otpInputStyle = css({
  width: '100%',
  minWidth: 0,
  height: 48,
  borderRadius: '50%',
  border: '2px solid #D8E0EC',
  background: '#F8FBFF',
  textAlign: 'center',
  fontSize: 20,
  fontWeight: 700,
  color: '#272727',
  outline: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:focus': {
    borderColor: '#f2a40b',
    boxShadow: '0 0 0 3px rgba(143, 169, 206, 0.18)',
  },
})
